import { useState } from 'react';

import { apiClient, type Opportunity, type OpportunityInlineEditPayload } from '../../lib/api';

type EditingCell = {
  opportunityId: string;
  column: string;
};

type Opts = {
  onPatched: (opportunity: Opportunity) => void;
};

/**
 * Inline-edit state machine for the pipeline table:
 *   startEdit({opp, col}) → exclusive editing pointer
 *   commit(opp, patch)    → PATCH /opportunities/:id, then onPatched()
 *
 * Errors surface back through the `error` state so the cell can briefly
 * highlight in red before reverting on next render.
 */
export function useInlineEdit({ onPatched }: Opts) {
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function startEdit(cell: EditingCell) {
    setEditing(cell);
    setError(null);
  }

  function cancel() {
    setEditing(null);
  }

  async function commit(opportunityId: string, patch: OpportunityInlineEditPayload) {
    // No fields touched (user blurred without typing) — just close the editor.
    if (Object.keys(patch).length === 0) {
      setEditing(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await apiClient.patchOpportunity(opportunityId, patch);
      onPatched(updated);
      setEditing(null);
    } catch (err) {
      setError((err as Error).message);
      // Keep editor open so the user can retry without losing context.
    } finally {
      setSaving(false);
    }
  }

  return { editing, error, saving, startEdit, cancel, commit };
}
